import { clientAgentId } from 'constants/history';
import { MessageType } from 'enums/MessageType';
import { StorageKey } from 'enums/StorageKey';
import { TransmitStatus } from 'enums/TransmitStatus';
import { WidgetFeature } from 'enums/WidgetFeature';
import { AgentData } from 'interfaces/AgentData';
import { BaseWsMessage } from 'interfaces/BaseWsMessage';
import { JsonObject } from 'interfaces/JsonObject';
import { MessageData, RenderMessageData } from 'interfaces/MessageData';
import { QuoteData } from 'interfaces/QuoteData';
import { entries, get, has, isEqual, isObjectLike, isUndefined, keys } from 'lodash-es';
import { createElement, ReactNode } from 'react';
import { DefaultRootState } from 'react-redux';
import { services } from 'services/container';

import { chatDateFormat, chatTimeFormat, convertBackendTimestamp } from './dateTime';
import { randomUuid } from './random';
import { urlRegex } from './regex';

export const prepareMessageText = (text?: string) => {
    return text && text.trim();
};

export const wsToChatMessage = (msg: JsonObject, receivedOnline = false): MessageData => {
    const id = msg.id?.toString();
    const agentId = msg.agent_id?.toString();

    const data = Object.entries<JsonObject>(msg.data || {})?.[0]?.[1];
    const { agentType } = msg;

    const {
        time,
        type,
        media_url,
        media_width,
        media_height,
        media_thumb,
        media_thumb_width,
        media_thumb_height,
        metadata,
        quote,
        files,
        is_favorite,
    } = data;
    let { text, uuid, reactions } = data;

    if (Array.isArray(reactions)) {
        reactions = reactions.map(({ client_id, ...reaction }) => ({
            ...reaction,
            clientId: client_id,
        }));
    }

    text = text?.replace('\r', '');
    text = prepareMessageText(text);

    uuid ??= id ?? randomUuid();

    const msgData: MessageData = {
        id,
        agentId,
        agentType,
        text,
        files,
        time,
        uuid,
        type,
        media_url,
        media_width,
        media_height,
        media_thumb,
        media_thumb_width,
        media_thumb_height,
        metadata,
        receivedOnline,
        quote,
        reactions,
        favorite: !!is_favorite,
    };

    if (msg.customType) {
        msgData.customType = msg.customType;
    }

    return msgData;
};

export const messageToRenderData = (msg: MessageData): RenderMessageData => {
    const frontendTime = convertBackendTimestamp(msg?.time || 0);
    return {
        ...msg,
        formattedTime: chatTimeFormat(frontendTime),
        formattedDate: chatDateFormat(frontendTime),
    };
};

export const msgStatus = (msgId: string, lastReadId: string, lastReceivedId: string) =>
    +msgId <= +lastReadId
        ? TransmitStatus.Read
        : +msgId <= +lastReceivedId
        ? TransmitStatus.Delivered
        : TransmitStatus.Sent;

export const getMessageMedia = (msg: MessageData) => {
    let media_url: string, media_thumb: string;

    switch (msg.type) {
        case MessageType.Extended: {
            const extMsgData = msg.metadata.extra.extended_message_slider;
            let header = extMsgData.header;

            if (!header) {
                header = extMsgData.pages?.find((page) => !!page.header)?.header;
            }

            media_url = header?.image?.media_url;
            media_thumb = header?.image?.media_thumb;

            break;
        }

        case MessageType.Files: {
            const sortedFiles = msg.files?.sort((f1, f2) => f1.type - f2.type);
            media_url = sortedFiles?.[0]?.media_url;
            media_thumb = sortedFiles?.[0]?.media_thumb;

            break;
        }

        default: {
            media_url = msg.media_url;
            media_thumb = msg.media_thumb;
        }
    }

    return { media_url, media_thumb };
};

export const messageToQuote = (msg: MessageData, text: string, agent?: AgentData): QuoteData => {
    const { media_url, media_thumb } = getMessageMedia(msg);

    return {
        message_id: msg.id && Number(msg.id),
        agent_name: agent && agent.id != clientAgentId ? agent.name : 'Вы',
        text,
        media_type: msg.type,
        media_url,
        media_thumb,
        time: msg.time,
    };
};

export const DefaultLinkComponent = ({ href, text }: { href: string; text: string }) =>
    createElement(
        'a',
        { href, target: /.ru\/(i|l|c)\//.test(href) ? '' : '_blank', rel: 'noreferrer' },
        text,
    );

export const renderLinks = (text: string, LinkComponent = DefaultLinkComponent) => {
    const matches = [...text.matchAll(urlRegex)];
    const elements: ReactNode[] = [];

    let curPos = 0;
    if (matches.length) {
        let key = 0;
        matches.forEach((match) => {
            if (match.index > -1) {
                const link = match[0];
                const hasProtocol = /https?:\/\//.test(link);
                const finalUrl = hasProtocol ? link : `//${link}`;
                if (match.index - curPos > 0) {
                    elements.push(text.substring(curPos, match.index));
                }
                elements.push(
                    createElement(LinkComponent, {
                        key: key.toString(36),
                        href: finalUrl,
                        text: link,
                        // @ts-expect-error incorrect types
                        onClick: (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        },
                    }),
                );
                curPos = match.index + link.length;
                key += 1;
            }
        });
    }

    if (curPos < text.length) {
        elements.push(text.substring(curPos, text.length));
    }

    return elements;
};

export const renderHtmlMessage = (text) => {
    const regexp = new RegExp(`(href=['"]${urlRegex.source})|(${urlRegex.source})`, 'g');
    return text.replace(regexp, (match, href) => {
        const isDeeplink = /.ru\/(i|l|c)\//.test(match);
        const hasProtocol = /https?:\/\//.test(match);
        const finalUrl = hasProtocol ? match : `//${match}`;

        return href
            ? match
            : `<a onclick="event.stopPropagation();" href="${finalUrl}" ${
                  isDeeplink ? '' : `target="_blank"`
              } rel="noreferrer">${match}</a>`;
    });
};

export const sortHistoryMessages = (messages: MessageData[]) => {
    return messages?.sort((msg1, msg2) => {
        if (msg1.time !== msg2.time) {
            return msg1.time - msg2.time;
        }

        const [id1, id2] = [parseInt(msg1.id), parseInt(msg2.id)];

        if (id1 && id2) {
            return id1 - id2;
        }

        return 0;
    });
};

const msgTypeVisibleInHistoryMap: Partial<Record<MessageType, boolean>> = {
    [MessageType.Text]: true,
    [MessageType.Image]: true,
    [MessageType.File]: true,
    [MessageType.Files]: true,
    [MessageType.Extended]: true,
    [MessageType.Voice]: true,
    [MessageType.SharingStart]: true,
    [MessageType.SharingRequest]: true,
    [MessageType.SharingStop]: true,
    [MessageType.SharingRemoteViewStop]: true,
    [MessageType.SharingRemoteViewStart]: true,
};

export const isMessageReadable = (msg: MessageData) => {
    if (!msgTypeVisibleInHistoryMap[msg.type]) return false;

    if (msg.type === MessageType.Text && !msg.text) return false;
    if (msg.type === MessageType.Voice && !msg.text) return false;
    if (msg.type === MessageType.Extended && !msg.metadata?.extra?.extended_message_slider)
        return false;

    return true;
};

export const isMessageCommand = (msg: MessageData) => {
    if (msg.type === MessageType.Tech && msg.metadata.command) {
        return true;
    }

    return false;
};

export const getPendingMessageFromStorage = (uuid: string) => {
    const pendingKey = services.vo.getHostStorageKey(StorageKey.PendingMessages);
    const pendingMessages = JSON.parse(localStorage.getItem(pendingKey) || '{}');

    return pendingMessages[uuid] as MessageData;
};

export const addPendingMessageToStorage = (msg: MessageData) => {
    const pendingKey = services.vo.getHostStorageKey(StorageKey.PendingMessages);
    const pendingMessages = JSON.parse(localStorage.getItem(pendingKey) || '{}');

    pendingMessages[msg.uuid] = { ...msg, status: TransmitStatus.Error };

    localStorage.setItem(pendingKey, JSON.stringify(pendingMessages));
};

export const removePendingMessageFromStorage = (uuid: string) => {
    const pendingKey = services.vo.getHostStorageKey(StorageKey.PendingMessages);
    const pendingMessages = JSON.parse(localStorage.getItem(pendingKey) || '{}');

    delete pendingMessages[uuid];

    localStorage.setItem(pendingKey, JSON.stringify(pendingMessages));
};

export const loadPendingMessagesFromStorage = () => {
    const pendingKey = services.vo.getHostStorageKey(StorageKey.PendingMessages);

    return JSON.parse(localStorage.getItem(pendingKey) || '{}') as { [uuid: string]: MessageData };
};

export const filterMessageByAgentId = (messages: MessageData, id: number): MessageData => {
    if (!messages) return null;
    return Object.fromEntries(
        (Object.entries(messages) as Array<[string, JsonObject]>).filter(
            ([, value]) => value?.agent_id !== id,
        ),
    ) as MessageData;
};

export const getTimeAlreadyAwaitedForOperatorMs = () =>
    +localStorage.getItem(StorageKey.TimeAlreadyAwaitedForOperatorMs) || 0;

export const getTimeAwayFromAwaitTimerChip = () => {
    const timestamp = +localStorage.getItem(StorageKey.OperatorAwaitTimerCleanupTimestamp) || 0;

    if (timestamp) {
        return new Date().getTime() - timestamp;
    }

    return 0;
};

export const getMaxAwaitTimeSec = (
    state: DefaultRootState,
    action?: BaseWsMessage<unknown> & { max_await_time_sec?: number },
) => {
    const isEnabledOperatorWaitingTimer = !!parseInt(
        String(state.features[WidgetFeature.OperatorWaitingTimer]),
    );

    return (
        isEnabledOperatorWaitingTimer && ((action?.max_await_time_sec ?? false) as number | false)
    );
};

export const determineFinalMaxAwaitTimeMs = (maxAwaitTimeMs: number | null) => {
    if (maxAwaitTimeMs) {
        const adjustment = 1750; // dismount offset;
        const timeAlreadyAwaitedForOperatorMs = getTimeAlreadyAwaitedForOperatorMs();
        const timeAwayFromAwaitTimerChip = getTimeAwayFromAwaitTimerChip();

        const finalMaxAwaitTimeMs =
            maxAwaitTimeMs -
            timeAlreadyAwaitedForOperatorMs -
            timeAwayFromAwaitTimerChip -
            adjustment;

        if (finalMaxAwaitTimeMs <= 0) {
            return 0;
        }

        return finalMaxAwaitTimeMs;
    }

    return maxAwaitTimeMs;
};

export const deepDiff = (fromObject, toObject) => {
    const changes = {};

    const buildPath = (path, obj, key) => (isUndefined(path) ? key : `${path}.${key}`);

    const walk = (
        fromObject: Record<string, unknown>,
        toObject: Record<string, unknown>,
        path?: string,
    ) => {
        for (const key of keys(fromObject)) {
            const currentPath = buildPath(path, fromObject, key);
            if (!has(toObject, key)) {
                changes[currentPath] = { from: get(fromObject, key) };
            }
        }

        for (const [key, to] of entries(toObject)) {
            const currentPath = buildPath(path, toObject, key);
            if (!has(fromObject, key) && to) {
                changes[currentPath] = { to };
            } else {
                const from = get(fromObject, key);
                if (!isEqual(from, to)) {
                    if (isObjectLike(to) && isObjectLike(from)) {
                        walk(from, to, currentPath);
                    } else {
                        changes[currentPath] = { from, to };
                    }
                }
            }
        }
    };

    walk(fromObject, toObject);

    return changes;
};

export const isObject = (obj) => {
    return obj && typeof obj === 'object' && !Array.isArray(obj);
};

export const deepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;

    if (!isObject(obj1) || !isObject(obj2)) return false;

    if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

    for (const key in obj1) {
        if (!obj2.hasOwnProperty(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
};
